import React, { ReactNode } from 'react';
import { RuleType, FieldData } from 'rc-field-form/lib/interface';
import { FormInstance } from 'antd';
import { RuleObject } from 'antd/es/form';
import { ESearchParams } from 'common/const/searchParams.enum';
import { EFormFieldMessage } from 'common/const/form.enum';
import { IFormValue } from 'common/models';
import { IGoodPosition } from 'entities/Goods/Goods.models';
import { ICategoryPosition } from 'entities/Category/Category.models';

export const getFormItemLabel = (name: string, unitOfMeasurement: string | null) => {
  return unitOfMeasurement ? `${name}, ${unitOfMeasurement}` : name;
};

export const rules = {
  required: (required = true, message: string = EFormFieldMessage.RequiredField) => ({ required, message }),
  type: (type: RuleType, message: string) => ({ type, message }),
  pattern: (pattern: RegExp, message: string, validateTrigger?: string) => ({ pattern, message, validateTrigger }),
  min: (min: number, message: string) => ({ min, message }),
  max: (max: number, message: string) => ({ max, message }),
  rangeFrom: (
    name: string | number,
    getFieldValue: (field: (string | number)[]) => string,
    setFields: (fields: FieldData[]) => void,
  ) => ({
    validator(_: RuleObject, value: string) {
      const toFieldValue = getFieldValue([name, 0, 'to']);
      const setFieldErrors = (errors?: string[]) => setFields([{ name: [name, 0, 'to'], errors }]);

      if (!value || toFieldValue === undefined || toFieldValue === null) {
        setFieldErrors();
        return Promise.resolve();
      }

      if (value && toFieldValue && value <= toFieldValue) {
        setFieldErrors();
        return Promise.resolve();
      }

      return Promise.reject(EFormFieldMessage.InvalidRange);
    },
  }),
  rangeTo: (
    name: string | number,
    getFieldValue: (field: (string | number)[]) => string,
    setFields: (fields: FieldData[]) => void,
  ) => ({
    validator(_: RuleObject, value: string) {
      const fromFieldValue = getFieldValue([name, 0, 'from']);
      const setFieldErrors = (errors?: string[]) => setFields([{ name: [name, 0, 'from'], errors }]);

      if (value === undefined || value === null || !fromFieldValue) {
        setFieldErrors();
        return Promise.resolve();
      }

      if (value && fromFieldValue && value >= fromFieldValue) {
        setFieldErrors();
        return Promise.resolve();
      }

      setFieldErrors([EFormFieldMessage.InvalidRange]);
      return Promise.reject();
    },
  }),
};

export const formValuesToPositions = (formValues: IFormValue): (IGoodPosition | ICategoryPosition)[] => {
  const positions: (IGoodPosition | ICategoryPosition)[] = [];

  Object.entries(formValues).forEach(([key, value]) => {
    const propertyId = Number(key);

    switch (true) {
      case key === ESearchParams.CatalogId: {
        break;
      }
      case typeof value === 'string': {
        positions.push({ propertyId, values: [value] });
        break;
      }
      case Array.isArray(value): {
        if (typeof value[0] === 'object') {
          const from = value[0].from;
          const to = value[0].to;

          if (from && to) {
            positions.push({ propertyId, range: { from, to } });
            break;
          } else if (from) {
            positions.push({ propertyId, range: { from } });
            break;
          } else if (to) {
            positions.push({ propertyId, range: { to } });
            break;
          }

          break;
        } else {
          if (value.length) {
            positions.push({ propertyId, values: value });
          }
          break;
        }
      }
      default: {
        break;
      }
    }
  });

  return positions;
};

export const checkForFormErrors = (form: FormInstance) => {
  const errors = form.getFieldsError().flatMap((item) => item.errors);

  return !!errors.length;
};

export const renderRequiredMark = (label: ReactNode, info: { required: boolean }) => {
  return (
    <label>
      {label}
      {info.required && <sup style={{ color: 'red', fontSize: 10, marginLeft: 3 }}>*</sup>}
    </label>
  );
};
